import React, { useEffect, useRef, useState } from 'react';
import { BackPill } from './BackPill';
import axios from 'axios';
import {
  ChevronLeft, ChevronRight, Sparkles, Trash2, TrendingUp, Sun, Zap,
  Eye, Compass, Check, Plus, MessageCircle,
} from 'lucide-react';
import { BACKEND_URL } from '../../utils/api';
import { useHistoryClose } from '../../hooks/useHistoryClose';
import { imperialSuccessCue, conspiracyLoggedCue } from '../../utils/pronoiaSounds';
import { useAuth } from '../../contexts/AuthContext';
import { PRONOIA_AFFIRMATIONS } from '../../data/pronoiaAffirmations';

/* ═══════════════════════════════════════════════════════════════════
   THE PRONOIA — Providence Engine
   Layout: FIXED top (ring + paradox) → FIXED tab switcher →
           FLEX middle (tab content, only scrolls if items overflow) →
           FIXED bottom (Add btn for Conspiracy / Slider+Seal for Reframe)
   Whole modal fits the viewport — no page scrolling.
   ═══════════════════════════════════════════════════════════════════ */

const GOLD = '#C5A059';
const GOLD_SOFT = '#8B6B2F';
const GOLD_BRIGHT = '#E9C77A';
const GOLD_HALO = '#FFF6D0';
const BG = '#050308';
const OBSIDIAN = '#0A0710';

const CATEGORIES = [
  { key: 'green_light',  label: 'Green Light',     Icon: Zap,           color: '#4ADE80' }, // emerald
  { key: 'helpful_text', label: 'Helpful Signal',  Icon: MessageCircle, color: '#60A5FA' }, // signal blue
  { key: 'energy_burst', label: 'Energy Burst',    Icon: Sun,           color: '#FBBF24' }, // amber
  { key: 'clarity',      label: 'Clarity',         Icon: Eye,           color: '#A78BFA' }, // violet
  { key: 'sign',         label: 'Sign',            Icon: Compass,       color: '#22D3EE' }, // cyan
  { key: 'other',        label: 'Other',           Icon: Sparkles,      color: '#E9C77A' }, // gold
];

/** local-time today key — must match backend's tz-aware date_key */
const todayLocalKey = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const RESET_PERIOD_OPTIONS = [
  { days: 1,   label: '1 day' },
  { days: 7,   label: '1 week' },
  { days: 30,  label: '1 month' },
  { days: 60,  label: '2 months' },
  { days: 90,  label: '3 months' },
  { days: 100, label: '100 days' },
];

const periodLabel = (days) =>
  (RESET_PERIOD_OPTIONS.find(o => o.days === days) || { label: `${days} days` }).label;

/* ═══ Providence Orb — 10 rings × 10 segments = 100 proofs
       • Each conspiracy the user forges lights one segment.
       • Rings fill from innermost outward, segments 1→10 clockwise.
       • Rings never touch each other (visible gap between them).
       • Center shows the user's profile picture (or a sigil).
       • On 100/100 → avatar bursts into a golden halo, then the
         next log flips everything back to dim and begins ring 1. ═══ */
const ProvidenceOrb = ({ totalConspiracies = 0, profilePic = null }) => {
  const size = 220;
  const cx = size / 2;
  const cy = size / 2;
  const centerR = 50;              // avatar circle radius (bigger profile)
  const thickness = 3;             // stroke width of each ring
  const gap = 2;                   // gap between rings (radial)
  const segGapDeg = 5;             // angular gap between segments
  const RINGS = 10;
  const SEGMENTS = 10;
  const TOTAL = RINGS * SEGMENTS;  // 100

  const cycleTotal = totalConspiracies;
  // progress inside the current cycle of 100. When user hits exactly a
  // multiple of 100 (including 0), we celebrate with ALL rings lit; the
  // NEXT log flips to 1 lit segment in ring 1.
  const inCycle = cycleTotal > 0 ? ((cycleTotal - 1) % TOTAL) + 1 : 0;
  const complete = cycleTotal > 0 && cycleTotal % TOTAL === 0;
  const litCount = complete ? TOTAL : inCycle;

  // Innermost ring i=0 sits just outside the avatar; outermost is i=RINGS-1.
  const ringCenterR = (i) => centerR + gap + thickness / 2 + i * (thickness + gap);
  const outerMostR  = ringCenterR(RINGS - 1) + thickness / 2;

  const arcPath = (r, startDeg, endDeg) => {
    const rad = (d) => (d - 90) * Math.PI / 180;
    const x1 = cx + r * Math.cos(rad(startDeg));
    const y1 = cy + r * Math.sin(rad(startDeg));
    const x2 = cx + r * Math.cos(rad(endDeg));
    const y2 = cy + r * Math.sin(rad(endDeg));
    const large = endDeg - startDeg > 180 ? 1 : 0;
    return `M ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2}`;
  };

  const initial = (() => {
    // fallback glyph if no picture — the Roman ∴ (therefore) as a subtle sigil
    return '∴';
  })();

  return (
    <div className="flex-shrink-0" style={{ width: size, height: size }} data-testid="pronoia-golden-ring">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <defs>
          <clipPath id="pronoia-avatar-clip">
            <circle cx={cx} cy={cy} r={centerR - 2}/>
          </clipPath>
          <radialGradient id="pronoia-orb-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0"   stopColor={GOLD_HALO} stopOpacity={complete ? 0.9 : 0.28}/>
            <stop offset="0.6" stopColor={GOLD}      stopOpacity={complete ? 0.55 : 0.12}/>
            <stop offset="1"   stopColor="transparent"/>
          </radialGradient>
          <linearGradient id="pronoia-seg-lit" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0"   stopColor={GOLD_HALO}/>
            <stop offset="0.5" stopColor={GOLD_BRIGHT}/>
            <stop offset="1"   stopColor={GOLD}/>
          </linearGradient>
        </defs>

        {/* Ambient background glow */}
        <circle cx={cx} cy={cy} r={outerMostR + 6} fill="url(#pronoia-orb-glow)"/>

        {/* Rings — drawn inner → outer so overlap ordering is neutral */}
        {Array.from({ length: RINGS }).map((_, i) => {
          const r = ringCenterR(i);
          return Array.from({ length: SEGMENTS }).map((_, j) => {
            const idx = i * SEGMENTS + j;             // 0..99
            const lit = idx < litCount;
            const startDeg = j * (360 / SEGMENTS) + segGapDeg / 2;
            const endDeg   = (j + 1) * (360 / SEGMENTS) - segGapDeg / 2;
            return (
              <path
                key={`ring-${i}-seg-${j}`}
                d={arcPath(r, startDeg, endDeg)}
                fill="none"
                stroke={lit ? 'url(#pronoia-seg-lit)' : `${GOLD}22`}
                strokeWidth={thickness}
                strokeLinecap="round"
                style={{
                  filter: lit ? `drop-shadow(0 0 3px ${GOLD}cc)` : 'none',
                  transition: 'stroke 500ms ease-out, filter 500ms ease-out',
                }}
                data-testid={`pronoia-seg-${idx}`}
                data-lit={lit ? 'true' : 'false'}
              />
            );
          });
        })}

        {/* Center avatar — profile pic when available */}
        <circle
          cx={cx} cy={cy} r={centerR}
          fill={OBSIDIAN}
          stroke={complete ? GOLD_HALO : GOLD}
          strokeWidth={complete ? 2 : 1.4}
          style={{
            filter: complete
              ? `drop-shadow(0 0 22px ${GOLD_HALO}) drop-shadow(0 0 10px ${GOLD_BRIGHT})`
              : `drop-shadow(0 0 6px ${GOLD}55)`,
            transition: 'filter 800ms ease-out, stroke 800ms ease-out',
          }}
        />
        {profilePic ? (
          <image
            href={profilePic}
            x={cx - (centerR - 2)} y={cy - (centerR - 2)}
            width={(centerR - 2) * 2} height={(centerR - 2) * 2}
            preserveAspectRatio="xMidYMid slice"
            clipPath="url(#pronoia-avatar-clip)"
            data-testid="pronoia-orb-avatar"
          />
        ) : (
          <text
            x={cx} y={cy + 6} textAnchor="middle" fontFamily="Cinzel"
            style={{
              fontSize: 24,
              fill: complete ? GOLD_HALO : GOLD_BRIGHT,
              letterSpacing: '0.08em',
            }}
          >
            {initial}
          </text>
        )}

        {/* Celebration sparkles on completion */}
        {complete && (
          <g style={{ transformOrigin: `${cx}px ${cy}px` }}>
            {[0, 72, 144, 216, 288].map((deg) => {
              const rad = (deg - 90) * Math.PI / 180;
              const px = cx + (outerMostR + 8) * Math.cos(rad);
              const py = cy + (outerMostR + 8) * Math.sin(rad);
              return (
                <circle key={deg} cx={px} cy={py} r={1.6}
                  fill={GOLD_HALO}
                  style={{ filter: `drop-shadow(0 0 4px ${GOLD_HALO})` }}/>
              );
            })}
          </g>
        )}
      </svg>
    </div>
  );
};

/* ═══ AFFIRMATION BEAM — loops through 100 positive affirmations one after
       another with a soft breathing glow. Each message shows for ~5s, fades,
       then the next one slides in. Purely client-side, zero cloud cost. ═══ */
const AffirmationBeam = () => {
  const [idx, setIdx] = useState(() => Math.floor(Math.random() * PRONOIA_AFFIRMATIONS.length));
  useEffect(() => {
    const iv = setInterval(() => {
      setIdx((i) => (i + 1) % PRONOIA_AFFIRMATIONS.length);
    }, 5200); // ~5s per message
    return () => clearInterval(iv);
  }, []);
  const message = PRONOIA_AFFIRMATIONS[idx];
  return (
    <div
      data-testid="pronoia-affirmation-beam"
      className="rounded-lg px-4 py-1.5 mx-auto relative overflow-hidden flex items-center justify-center pronoia-affirmation-box"
      style={{
        background: `linear-gradient(160deg, ${OBSIDIAN}, ${BG})`,
        border: `1px solid ${GOLD}66`,
        maxWidth: '92%',
      }}
    >
      {/* Breathing beam — a horizontal light beam that sweeps across the
          box and pulses in opacity to feel alive. */}
      <span className="pronoia-affirmation-beam" aria-hidden/>
      <p
        key={idx}
        className="font-['Cormorant_Garamond'] italic pronoia-affirmation-text text-center"
        style={{
          color: GOLD_HALO,
          fontSize: 14,
          lineHeight: 1.3,
          textShadow: `0 0 12px ${GOLD}66`,
          position: 'relative',
          zIndex: 1,
          margin: 0,
        }}
        data-testid="pronoia-affirmation-text"
      >
        &ldquo;{message}&rdquo;
      </p>
    </div>
  );
};

/* ═══ CONSPIRACY tab body (middle scrollable + fixed footer input) ═══ */
const ConspiracyTab = ({ conspiracies, cat, setCat, text, setText, onLog, onDelete, busy,
                        periodDays = 7, daysRemaining = 0, onOpenSettings, logToast = '' }) => {
  // Backend already filters conspiracies to current period — display as-is
  const currentCatColor = (CATEGORIES.find(c => c.key === cat) || CATEGORIES[5]).color;

  return (
    <>
      <div className="flex-1 min-h-0 overflow-y-auto scrollbar-hide px-4 pt-5 pb-2">
        <div className="flex items-center justify-between gap-2 mb-1.5">
          <div className="flex items-center gap-2">
            <Zap size={13} style={{ color: GOLD_BRIGHT }}/>
            <p className="font-['Cinzel'] uppercase tracking-[0.28em]"
              style={{ color: GOLD_BRIGHT, fontSize: 11 }}>Conspiracy of Good</p>
          </div>
          <button type="button" onClick={onOpenSettings}
            data-testid="pronoia-period-btn"
            className="font-['Outfit'] uppercase tracking-[0.22em] rounded-full px-2.5 py-1 flex items-center gap-1"
            style={{
              background: 'rgba(197,160,89,0.1)',
              border: `1px solid ${GOLD}55`,
              color: GOLD, fontSize: 8,
            }}>
            <TrendingUp size={9}/> {periodLabel(periodDays)}
            {periodDays > 1 && ` · ${daysRemaining}d left`}
          </button>
        </div>
        <p className="font-['Outfit'] italic mb-2"
          style={{ color: 'rgba(255,255,255,0.55)', fontSize: 10.5, lineHeight: 1.4 }}>
          Pinpoint one proof the Universe is working for you — a green light, a helpful text, a burst of energy, a moment of clarity.
        </p>

        {/* Category chips — 3-column grid (2 rows of 3), no horizontal scroll */}
        <div className="grid grid-cols-3 gap-1.5 mb-2"
          data-testid="pronoia-category-picker">
          {CATEGORIES.map(c => {
            const active = cat === c.key;
            return (
              <button key={c.key} type="button" onClick={() => setCat(c.key)}
                data-testid={`pronoia-cat-${c.key}`}
                className="rounded-full px-2 py-1.5 font-['Outfit'] uppercase tracking-[0.14em] flex items-center justify-center gap-1"
                style={{
                  background: active
                    ? `linear-gradient(180deg, ${c.color}, ${c.color}77)`
                    : `${c.color}12`,
                  color: active ? '#0a0a0a' : `${c.color}dd`,
                  border: `1px solid ${c.color}${active ? 'ff' : '66'}`,
                  fontSize: 9,
                  boxShadow: active ? `0 0 10px ${c.color}88` : 'none',
                }}>
                <c.Icon size={10}/> {c.label}
              </button>
            );
          })}
        </div>

        {/* Period-filtered list */}
        {conspiracies.length === 0 ? (
          <p className="font-['Outfit'] italic text-center py-3"
            style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>
            (No conspiracies logged this {periodLabel(periodDays).toLowerCase()} — capture your first proof of providence)
          </p>
        ) : (
          <div className="space-y-1.5" data-testid="pronoia-conspiracy-list">
            {conspiracies.map(c => {
              const catObj = CATEGORIES.find(x => x.key === c.category) || CATEGORIES[5];
              return (
                <div key={c.entry_id}
                  className="flex items-start gap-2 rounded-lg px-3 py-2"
                  style={{
                    background: `linear-gradient(135deg, ${catObj.color}18, ${catObj.color}05)`,
                    border: `1px solid ${catObj.color}66`,
                    boxShadow: `0 0 6px ${catObj.color}22`,
                  }}
                  data-testid={`pronoia-conspiracy-item-${c.entry_id}`}>
                  <catObj.Icon size={13}
                    style={{ color: catObj.color, marginTop: 2, flexShrink: 0,
                      filter: `drop-shadow(0 0 4px ${catObj.color}77)` }}/>
                  <p className="flex-1 font-['Outfit']"
                    style={{ color: 'rgba(255,255,255,0.92)', fontSize: 12.5, lineHeight: 1.4 }}>
                    {c.text}
                  </p>
                  <button type="button" onClick={() => onDelete(c.entry_id)}
                    data-testid={`pronoia-conspiracy-delete-${c.entry_id}`}
                    className="opacity-60 hover:opacity-100 flex-shrink-0">
                    <Trash2 size={12} style={{ color: '#D87979' }}/>
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* FIXED footer: input + add — border color reflects current category */}
      <div className="flex-shrink-0 px-3 pt-2 border-t relative"
        style={{
          borderColor: `${GOLD}22`,
          background: 'linear-gradient(180deg, transparent, rgba(0,0,0,0.5))',
          paddingBottom: 'calc(var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)) + 12px)',
        }}>
        {logToast && (
          <div className="absolute left-0 right-0 pronoia-log-toast pointer-events-none"
            style={{ bottom: 'calc(100% + 6px)' }}
            data-testid="pronoia-log-toast">
            <div className="mx-auto rounded-full px-4 py-1.5 font-['Cinzel'] uppercase tracking-[0.24em] inline-flex items-center gap-1.5"
              style={{
                background: `linear-gradient(180deg, ${GOLD_BRIGHT}, ${GOLD_SOFT})`,
                color: '#0a0a0a',
                border: `1px solid ${GOLD_HALO}`,
                boxShadow: `0 0 24px ${GOLD}aa, 0 0 44px ${GOLD}44`,
                fontSize: 10,
              }}>
              <Sparkles size={11} strokeWidth={2.5}/> {logToast}
            </div>
          </div>
        )}
        <div className="flex gap-2">
          <input type="text" value={text}
            onChange={(e) => setText(e.target.value.slice(0, 800))}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onLog(); } }}
            placeholder="I noticed..."
            data-testid="pronoia-conspiracy-input"
            className="flex-1 rounded-lg px-3 py-2.5 font-['Outfit']"
            style={{
              background: 'rgba(255,255,255,0.04)',
              border: `1px solid ${currentCatColor}66`,
              color: 'rgba(255,255,255,0.9)',
              fontSize: 13, outline: 'none',
              boxShadow: `inset 0 0 8px ${currentCatColor}18`,
            }}/>
          <button type="button" onClick={onLog}
            disabled={!text.trim() || busy}
            data-testid="pronoia-conspiracy-add"
            className="rounded-lg px-4 flex items-center justify-center disabled:opacity-40"
            style={{
              background: `linear-gradient(180deg, ${currentCatColor}, ${currentCatColor}77)`,
              color: '#0a0a0a', border: `1px solid ${currentCatColor}`,
              boxShadow: `0 0 12px ${currentCatColor}66`, minWidth: 52,
            }}>
            <Plus size={18} strokeWidth={2.5}/>
          </button>
        </div>
      </div>
    </>
  );
};

/* ═══ REFRAME tab body (middle: fields, fixed footer: slider + seal) ═══ */
const ReframeTab = ({ obstacle, setObstacle, opportunity, setOpportunity,
                     slider, setSlider, transformed, setTransformed,
                     onCommit, saving, logToast = '' }) => {
  useEffect(() => {
    if (slider >= 92 && obstacle.trim() && opportunity.trim() && !transformed) {
      setTransformed(true);
      imperialSuccessCue();
    }
    if (slider < 92 && transformed) setTransformed(false);
  }, [slider, obstacle, opportunity, transformed, setTransformed]);

  return (
    <>
      <div className="flex-1 min-h-0 overflow-y-auto scrollbar-hide px-4 pt-3 pb-2">
        <div className="flex items-center gap-2 mb-1.5">
          <TrendingUp size={13} style={{ color: GOLD_BRIGHT }}/>
          <p className="font-['Cinzel'] uppercase tracking-[0.28em]"
            style={{ color: GOLD_BRIGHT, fontSize: 11 }}>Imperial Reframe</p>
        </div>
        <p className="font-['Outfit'] italic mb-2"
          style={{ color: 'rgba(255,255,255,0.55)', fontSize: 10.5, lineHeight: 1.4 }}>
          Slide the obstacle right — watch it transmute into strategic advantage.
        </p>

        <label className="font-['Outfit'] uppercase tracking-[0.24em] block mb-1"
          style={{ color: '#D87979', fontSize: 9 }}>Obstacle</label>
        <textarea value={obstacle}
          onChange={(e) => setObstacle(e.target.value.slice(0, 800))}
          placeholder="The situation that felt like a setback..."
          rows={3}
          data-testid="pronoia-reframe-obstacle"
          className="w-full rounded-lg px-3 py-2 font-['Outfit'] resize-none mb-3"
          style={{
            background: 'rgba(200,80,80,0.06)',
            border: '1px solid rgba(216,121,121,0.55)',
            color: 'rgba(255,255,255,0.9)',
            fontSize: 13, outline: 'none',
          }}/>

        <label className="font-['Outfit'] uppercase tracking-[0.24em] block mb-1"
          style={{ color: GOLD_BRIGHT, fontSize: 9 }}>Imperial Opportunity</label>
        <textarea value={opportunity}
          onChange={(e) => setOpportunity(e.target.value.slice(0, 800))}
          placeholder="The hidden benefit — the strategic advantage this unlocks..."
          rows={3}
          data-testid="pronoia-reframe-opportunity"
          className="w-full rounded-lg px-3 py-2 font-['Outfit'] resize-none"
          style={{
            background: `${GOLD}0A`,
            border: `1px solid ${GOLD}88`,
            color: 'rgba(255,255,255,0.9)',
            fontSize: 13, outline: 'none',
          }}/>
      </div>

      {/* FIXED footer: slider + seal button */}
      <div className="flex-shrink-0 px-3 pt-2 border-t relative"
        style={{
          borderColor: `${GOLD}22`,
          background: 'linear-gradient(180deg, transparent, rgba(0,0,0,0.5))',
          paddingBottom: 'calc(var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)) + 12px)',
        }}>
        {logToast && (
          <div className="absolute left-0 right-0 pronoia-log-toast pointer-events-none"
            style={{ bottom: 'calc(100% + 6px)' }}
            data-testid="pronoia-reframe-toast">
            <div className="mx-auto rounded-full px-4 py-1.5 font-['Cinzel'] uppercase tracking-[0.24em] inline-flex items-center gap-1.5"
              style={{
                background: `linear-gradient(180deg, ${GOLD_BRIGHT}, ${GOLD_SOFT})`,
                color: '#0a0a0a',
                border: `1px solid ${GOLD_HALO}`,
                boxShadow: `0 0 24px ${GOLD}aa, 0 0 44px ${GOLD}44`,
                fontSize: 10,
              }}>
              <Sparkles size={11} strokeWidth={2.5}/> {logToast}
            </div>
          </div>
        )}
        <div className="relative rounded-full h-9 mb-2 overflow-hidden transition-opacity"
          style={{
            background: `linear-gradient(90deg, rgba(200,80,80,0.16) 0%, rgba(200,80,80,0.08) 40%, ${GOLD}22 60%, ${GOLD}44 100%)`,
            border: `1px solid ${GOLD}77`,
            opacity: (obstacle.trim() && opportunity.trim()) ? 1 : 0.4,
            pointerEvents: (obstacle.trim() && opportunity.trim()) ? 'auto' : 'none',
          }}
          data-testid="pronoia-reframe-slider-track">
          <div className="absolute inset-y-0 left-0 transition-all"
            style={{
              width: `${slider}%`,
              background: `linear-gradient(90deg, ${GOLD}22 0%, ${GOLD_BRIGHT}66 100%)`,
              boxShadow: `inset 0 0 20px ${GOLD}55`,
            }}/>
          <input type="range" min="0" max="100" value={slider}
            onChange={(e) => setSlider(Number(e.target.value))}
            disabled={!obstacle.trim() || !opportunity.trim()}
            data-testid="pronoia-reframe-slider"
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"/>
          {/* Thumb — pinned inside the track so it stays fully visible at 0% and 100% */}
          <div className="absolute top-1/2 rounded-full flex items-center justify-center pointer-events-none"
            style={{
              left: `calc((100% - 32px) * ${slider} / 100)`,
              width: 32, height: 32, marginTop: -16,
              background: `linear-gradient(180deg, ${GOLD_BRIGHT}, ${GOLD_SOFT})`,
              border: `1.5px solid ${GOLD_HALO}`,
              boxShadow: `0 0 14px ${GOLD}bb, inset 0 0 6px ${GOLD_HALO}88`,
              transition: 'left 120ms ease-out',
            }}>
            <ChevronRight size={18} strokeWidth={3} style={{ color: '#0a0a0a' }}/>
          </div>
          {/* "Obstacle" label — kept clear of the thumb even at slider=0 */}
          <div className="absolute inset-y-0 flex items-center pointer-events-none"
            style={{ left: 42 }}>
            <span className="font-['Outfit'] uppercase tracking-[0.22em]"
              style={{ color: '#D87979', fontSize: 8, opacity: slider < 40 ? 1 : 0.3 }}>
              Obstacle
            </span>
          </div>
          {/* "Imperial" label — kept clear of the thumb even at slider=100 */}
          <div className="absolute inset-y-0 flex items-center pointer-events-none"
            style={{ right: 42 }}>
            <span className="font-['Outfit'] uppercase tracking-[0.22em]"
              style={{ color: GOLD_BRIGHT, fontSize: 8, opacity: slider > 60 ? 1 : 0.3 }}>
              Imperial
            </span>
          </div>
        </div>

        <button type="button" onClick={onCommit}
          disabled={!transformed || saving}
          data-testid="pronoia-reframe-commit"
          className="w-full rounded-xl py-2.5 font-['Cinzel'] uppercase tracking-[0.28em] flex items-center justify-center gap-2 disabled:opacity-40"
          style={{
            background: transformed
              ? `linear-gradient(180deg, ${GOLD_BRIGHT}, ${GOLD_SOFT})`
              : 'rgba(255,255,255,0.03)',
            color: transformed ? '#0a0a0a' : 'rgba(255,255,255,0.35)',
            border: `1px solid ${transformed ? GOLD : 'rgba(255,255,255,0.14)'}`,
            fontSize: 11,
            boxShadow: transformed ? `0 0 20px ${GOLD}88` : 'none',
            animation: transformed ? 'pronoiaCommitGlow 1.8s ease-in-out infinite' : 'none',
          }}>
          <Sparkles size={13}/>
          {saving ? 'Sealing…'
            : transformed ? 'Seal Reframe · Log Victory'
            : (!obstacle.trim() || !opportunity.trim())
              ? 'Fill both fields, then slide right'
              : 'Slide handle right to Imperial'}
        </button>
      </div>
    </>
  );
};

/* ═══ Period picker overlay ═══ */
const PeriodPickerModal = ({ current, onPick, onClose }) => (
  <div className="fixed inset-0 z-[240] flex items-end sm:items-center justify-center"
    style={{ background: 'rgba(0,0,0,0.75)' }}
    data-testid="pronoia-period-modal">
    <div className="w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-4"
      style={{
        background: `linear-gradient(180deg, ${OBSIDIAN}, ${BG})`,
        border: `1.5px solid ${GOLD}88`,
        boxShadow: `0 0 40px ${GOLD}44`,
        paddingBottom: 'calc(var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)) + 20px)',
      }}>
      <p className="font-['Cinzel'] uppercase tracking-[0.28em] text-center mb-1"
        style={{ color: GOLD_BRIGHT, fontSize: 13, textShadow: `0 0 10px ${GOLD}55` }}>
        Reset Period
      </p>
      <p className="font-['Outfit'] italic text-center mb-4"
        style={{ color: 'rgba(255,255,255,0.55)', fontSize: 11 }}>
        When the period ends, BOTH the daily list AND the Golden Ring reset freshly.
      </p>
      <div className="space-y-1.5">
        {RESET_PERIOD_OPTIONS.map(o => {
          const active = o.days === current;
          return (
            <button key={o.days} type="button"
              onClick={() => onPick(o.days)}
              data-testid={`pronoia-period-opt-${o.days}`}
              className="w-full rounded-lg py-2.5 px-3 font-['Cinzel'] uppercase tracking-[0.22em] flex items-center justify-between"
              style={{
                background: active
                  ? `linear-gradient(180deg, ${GOLD_BRIGHT}, ${GOLD_SOFT})`
                  : `${GOLD}0A`,
                color: active ? '#0a0a0a' : `${GOLD}dd`,
                border: `1px solid ${GOLD}${active ? 'ff' : '44'}`,
                fontSize: 11,
              }}>
              <span>{o.label}</span>
              {active && <Check size={13} strokeWidth={3}/>}
            </button>
          );
        })}
      </div>
      <button type="button" onClick={onClose}
        data-testid="pronoia-period-close"
        className="w-full mt-3 rounded-lg py-2 font-['Outfit'] uppercase tracking-[0.22em]"
        style={{
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.14)',
          color: 'rgba(255,255,255,0.55)', fontSize: 10,
        }}>Close</button>
    </div>
  </div>
);

/* ═══ Main modal ═══ */
export const ThePronoia = ({ onClose }) => {
  const { user } = useAuth();
  const profilePic = user?.profile_picture || null;
  useHistoryClose(true, onClose);
  const [activeTab, setActiveTab] = useState('conspiracy'); // 'conspiracy' | 'reframe'
  const [stats, setStats] = useState({
    total_conspiracies: 0, ring_level: 0, progress_in_level: 0, streak_days: 0,
    period_days: 7, days_remaining: 0,
  });
  const [paradox, setParadox] = useState(null);
  const [conspiracies, setConspiracies] = useState([]);
  const [periodPickerOpen, setPeriodPickerOpen] = useState(false);

  // Conspiracy input state (lifted to modal so switching tabs preserves draft)
  const [text, setText] = useState('');
  const [cat, setCat] = useState('green_light');
  const [busy, setBusy] = useState(false);

  // Reframe state (lifted for same reason)
  const [obstacle, setObstacle] = useState('');
  const [opportunity, setOpportunity] = useState('');
  const [slider, setSlider] = useState(0);
  const [transformed, setTransformed] = useState(false);
  const [saving, setSaving] = useState(false);

  // Positive-text toast shown briefly after a proof is forged.
  const [logToast, setLogToast] = useState('');
  const logToastTimerRef = useRef(null);
  const flashToast = (msg) => {
    setLogToast(msg);
    if (logToastTimerRef.current) clearTimeout(logToastTimerRef.current);
    logToastTimerRef.current = setTimeout(() => setLogToast(''), 2400);
  };
  useEffect(() => () => { if (logToastTimerRef.current) clearTimeout(logToastTimerRef.current); }, []);

  const refreshStats = async () => {
    try {
      const s = await axios.get(`${BACKEND_URL}/api/pronoia/stats`);
      setStats(s.data);
    } catch { /* noop */ }
  };

  useEffect(() => {
    (async () => {
      try {
        const [s, p, c] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/pronoia/stats`),
          axios.get(`${BACKEND_URL}/api/pronoia/paradox`),
          axios.get(`${BACKEND_URL}/api/pronoia/conspiracies`),
        ]);
        setStats(s.data);
        setParadox(p.data);
        setConspiracies(c.data || []);
      } catch { /* noop */ }
    })();
  }, []);

  // Midnight rollover — re-fetch stats + paradox + conspiracies when the local
  // day changes. This makes the today-only list "reset" automatically without
  // any user action.
  const lastDayRef = useRef(todayLocalKey());
  useEffect(() => {
    const iv = setInterval(async () => {
      const t = todayLocalKey();
      if (t === lastDayRef.current) return;
      lastDayRef.current = t;
      try {
        const [s, p, c] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/pronoia/stats`),
          axios.get(`${BACKEND_URL}/api/pronoia/paradox`),
          axios.get(`${BACKEND_URL}/api/pronoia/conspiracies`),
        ]);
        setStats(s.data);
        setParadox(p.data);
        setConspiracies(c.data || []);
      } catch { /* noop */ }
    }, 30_000); // check twice a minute — cheap
    return () => clearInterval(iv);
  }, []);

  /* When any Pronoia ring action lights up today's ring, mark the
     profile-gold flag so <AuraRing/> shows the FULL GOLD upper half
     for the rest of today.  Also emits a one-time "golden energy"
     toast the very first time the ring lights up today. */
  const activateProfileGoldForToday = () => {
    try {
      const d = new Date();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      const today = `${d.getFullYear()}-${mm}-${dd}`;
      const priorRaw = localStorage.getItem('pronoia_gold_today_v1');
      let prior = null;
      try { prior = priorRaw ? JSON.parse(priorRaw) : null; } catch (_) { /* noop */ }
      const firstTimeToday = !prior || prior.date !== today;
      localStorage.setItem('pronoia_gold_today_v1', JSON.stringify({ date: today }));
      // Nudge any mounted <AuraRing/> to re-read via the storage event.
      try {
        window.dispatchEvent(new StorageEvent('storage', {
          key: 'pronoia_gold_today_v1',
          newValue: JSON.stringify({ date: today }),
        }));
      } catch (_) { /* noop */ }
      if (firstTimeToday) {
        flashToast('Golden energy added to your profile today ✦');
      }
    } catch (_) { /* noop */ }
  };

  const handleLogConspiracy = async () => {
    if (!text.trim() || busy) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/pronoia/conspiracy`,
        { text: text.trim(), category: cat });
      setConspiracies(prev => [data, ...prev]);
      setText('');
      conspiracyLoggedCue();
      window.dispatchEvent(new CustomEvent('pronoia:logged'));
      // Positive-text feedback — brief golden toast above the input.
      const RECEIPTS = [
        'Ring energy received ✦',
        'Providence logged',
        'One more proof · segment lit',
        'The Universe hears you',
        'Signal received · ring brightens',
      ];
      flashToast(RECEIPTS[Math.floor(Math.random() * RECEIPTS.length)]);
      activateProfileGoldForToday();
      await refreshStats();
    } catch { /* noop */ }
    setBusy(false);
  };

  const handleDeleteConspiracy = async (entry_id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/pronoia/conspiracy/${entry_id}`);
      setConspiracies(prev => prev.filter(c => c.entry_id !== entry_id));
      window.dispatchEvent(new CustomEvent('pronoia:logged'));
      await refreshStats();
    } catch { /* noop */ }
  };

  const handleCommitReframe = async () => {
    if (saving || !transformed || !obstacle.trim() || !opportunity.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/pronoia/reframe`,
        { obstacle: obstacle.trim(), opportunity: opportunity.trim() });
      setObstacle(''); setOpportunity(''); setSlider(0); setTransformed(false);
      // Positive-text feedback — reframe also lights a ring segment.
      const REFRAME_RECEIPTS = [
        'Reframe sealed · segment lit ✦',
        'Obstacle transmuted · ring brightens',
        'Imperial reframe received',
        'Providence logged · gold added',
        'Strategic advantage sealed',
      ];
      flashToast(REFRAME_RECEIPTS[Math.floor(Math.random() * REFRAME_RECEIPTS.length)]);
      activateProfileGoldForToday();
      await refreshStats();
    } catch { /* noop */ }
    setSaving(false);
  };

  const handleMarkParadoxSeen = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/pronoia/paradox/seen`);
      setParadox(prev => prev ? { ...prev, seen_today: true } : prev);
    } catch { /* noop */ }
  };

  const handlePickPeriod = async (days) => {
    try {
      await axios.post(`${BACKEND_URL}/api/pronoia/settings`, { reset_period_days: days });
      // Refetch stats + list with new period filter
      const [s, c] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/pronoia/stats`),
        axios.get(`${BACKEND_URL}/api/pronoia/conspiracies`),
      ]);
      setStats(s.data);
      setConspiracies(c.data || []);
      setPeriodPickerOpen(false);
    } catch { /* noop */ }
  };

  return (
    <div className="fixed inset-0 z-[220] flex flex-col"
      style={{ background: BG }} data-testid="pronoia-modal">
      {/* Header */}
      <div className="flex-shrink-0 flex items-center justify-between px-3 py-2.5 border-b"
        style={{ borderColor: `${GOLD}22` }}>
        <BackPill onClick={onClose} accent="#FFD98A" testId="pronoia-close-btn"/>
        <p className="font-['Cinzel'] tracking-[0.22em]"
          style={{ color: '#FFE9A0', fontSize: 13, textShadow: `0 0 10px ${GOLD}55` }}>
          PRONOIA
        </p>
        <span className="w-14"/>
      </div>

      {/* FIXED top section — orb + right-side stats + paradox strip below */}
      <div className="flex-shrink-0 px-3 pt-2 pb-2 space-y-2">
        <div className="flex items-center gap-4" data-testid="pronoia-top-block">
          <ProvidenceOrb
            totalConspiracies={(stats.total_conspiracies || 0) + (stats.total_reframes || 0)}
            profilePic={profilePic}/>
          {/* Right-side stats column */}
          <div className="flex-1 min-w-0 flex flex-col gap-1.5">
            <div>
              <p className="font-['Cinzel'] tracking-[0.28em]"
                style={{ color: GOLD_BRIGHT, fontSize: 11, textShadow: `0 0 10px ${GOLD}44` }}>
                THE UNIVERSE FAVORS YOU
              </p>
              <p className="font-['Outfit'] uppercase tracking-[0.22em] mt-0.5"
                style={{ color: GOLD_SOFT, fontSize: 8 }}>
                Providence · Detected · Amplified
              </p>
            </div>
            <div className="flex items-center gap-1.5 mt-1" data-testid="pronoia-proofs">
              <Sparkles size={11} style={{ color: GOLD_BRIGHT }}/>
              <span className="font-['Outfit'] uppercase tracking-[0.22em]"
                style={{ color: GOLD_BRIGHT, fontSize: 9 }}>
                {(stats.total_conspiracies || 0) + (stats.total_reframes || 0)} proofs · {Math.floor(((stats.total_conspiracies || 0) + (stats.total_reframes || 0)) / 100)} cycles
              </span>
            </div>
            <div className="flex items-center gap-1.5" data-testid="pronoia-streak">
              <TrendingUp size={11} style={{ color: GOLD_BRIGHT }}/>
              <span className="font-['Outfit'] uppercase tracking-[0.22em]"
                style={{ color: GOLD_BRIGHT, fontSize: 9 }}>
                {stats.streak_days} day streak
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <Zap size={11} style={{ color: GOLD }}/>
              <span className="font-['Outfit'] uppercase tracking-[0.22em]"
                style={{ color: `${GOLD}dd`, fontSize: 9 }}>
                {(() => {
                  const t = (stats.total_conspiracies || 0) + (stats.total_reframes || 0);
                  const inCycle = t > 0 ? ((t - 1) % 100) + 1 : 0;
                  const isComplete = t > 0 && t % 100 === 0;
                  if (isComplete) return 'CYCLE COMPLETE ✦';
                  const remaining = 100 - inCycle;
                  return `${remaining} to full circle`;
                })()}
              </span>
            </div>
          </div>
        </div>
        <AffirmationBeam/>
      </div>

      {/* FIXED tab switcher — full-boxed pill tabs with clearance below */}
      <div className="flex-shrink-0 px-3 pt-2.5 pb-2 border-b"
        style={{ borderColor: `${GOLD}18`, overflow: 'visible' }}>
        <div className="flex gap-1.5" data-testid="pronoia-tabs">
          {[
            { key: 'conspiracy', label: 'Conspiracy of Good', Icon: Zap },
            { key: 'reframe',    label: 'Imperial Reframe',   Icon: TrendingUp },
          ].map(t => {
            const active = activeTab === t.key;
            return (
              <button key={t.key} type="button"
                onClick={() => setActiveTab(t.key)}
                data-testid={`pronoia-tab-${t.key}`}
                className="flex-1 rounded-lg py-2.5 font-['Cinzel'] uppercase tracking-[0.18em] flex items-center justify-center gap-1.5 transition-all"
                style={{
                  background: active
                    ? `linear-gradient(180deg, ${GOLD_BRIGHT}, ${GOLD_SOFT})`
                    : 'rgba(255,255,255,0.03)',
                  color: active ? '#0a0a0a' : `${GOLD}dd`,
                  border: `1px solid ${GOLD}${active ? 'ff' : '44'}`,
                  fontSize: 10,
                  boxShadow: active ? `0 0 12px ${GOLD}55` : 'none',
                }}>
                <t.Icon size={11}/> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* FLEX middle — tab content + FIXED bottom action bar (both inside the tab) */}
      {activeTab === 'conspiracy' ? (
        <ConspiracyTab
          conspiracies={conspiracies}
          cat={cat} setCat={setCat}
          text={text} setText={setText}
          onLog={handleLogConspiracy}
          onDelete={handleDeleteConspiracy}
          busy={busy}
          periodDays={stats.period_days || 7}
          daysRemaining={stats.days_remaining || 0}
          onOpenSettings={() => setPeriodPickerOpen(true)}
          logToast={logToast}/>
      ) : (
        <ReframeTab
          obstacle={obstacle} setObstacle={setObstacle}
          opportunity={opportunity} setOpportunity={setOpportunity}
          slider={slider} setSlider={setSlider}
          transformed={transformed} setTransformed={setTransformed}
          onCommit={handleCommitReframe} saving={saving}
          logToast={logToast}/>
      )}

      {periodPickerOpen && (
        <PeriodPickerModal
          current={stats.period_days || 7}
          onPick={handlePickPeriod}
          onClose={() => setPeriodPickerOpen(false)}/>
      )}

      <style>{`
        @keyframes pronoiaCommitGlow {
          0%, 100% { box-shadow: 0 0 20px ${GOLD}88; }
          50%      { box-shadow: 0 0 34px ${GOLD_HALO}, 0 0 60px ${GOLD}66; }
        }
        @keyframes pronoiaBoxBreath {
          0%, 100% { box-shadow: 0 0 12px ${GOLD}22, inset 0 0 10px ${GOLD}18; border-color: ${GOLD}55; }
          50%      { box-shadow: 0 0 26px ${GOLD}88, inset 0 0 18px ${GOLD}44; border-color: ${GOLD_BRIGHT}; }
        }
        @keyframes pronoiaBeamSweep {
          0%   { transform: translateX(-120%); opacity: 0; }
          20%  { opacity: 0.9; }
          60%  { opacity: 0.9; }
          100% { transform: translateX(120%); opacity: 0; }
        }
        @keyframes pronoiaTextFade {
          0%   { opacity: 0; transform: translateY(6px); filter: blur(2px); }
          15%  { opacity: 1; transform: translateY(0);  filter: blur(0); }
          85%  { opacity: 1; }
          100% { opacity: 0.15; }
        }
        .pronoia-affirmation-box {
          animation: pronoiaBoxBreath 4.2s ease-in-out infinite;
        }
        .pronoia-affirmation-beam {
          position: absolute;
          top: 0; bottom: 0; left: 0;
          width: 45%;
          background: linear-gradient(
            90deg,
            transparent 0%,
            ${GOLD_HALO}55 40%,
            ${GOLD_HALO}aa 50%,
            ${GOLD_HALO}55 60%,
            transparent 100%
          );
          filter: blur(6px);
          animation: pronoiaBeamSweep 5.2s ease-in-out infinite;
          pointer-events: none;
          z-index: 0;
        }
        .pronoia-affirmation-text {
          animation: pronoiaTextFade 5.2s ease-in-out;
        }
        @keyframes pronoiaToastRise {
          0%   { opacity: 0; transform: translateY(8px) scale(0.94); }
          15%  { opacity: 1; transform: translateY(0) scale(1); }
          80%  { opacity: 1; transform: translateY(0) scale(1); }
          100% { opacity: 0; transform: translateY(-4px) scale(0.98); }
        }
        .pronoia-log-toast {
          text-align: center;
          animation: pronoiaToastRise 2400ms ease-out both;
        }
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
};

/* Pronoia tile icon — expanding golden ring with a small sun */
export const PronoiaTileIcon = ({ size = 26 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24"
    style={{ filter: 'drop-shadow(0 0 8px rgba(233,199,122,0.75))' }}>
    <defs>
      <linearGradient id="prIcon" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0" stopColor="#FFF6D0"/>
        <stop offset="0.55" stopColor="#E9C77A"/>
        <stop offset="1" stopColor="#7A5722"/>
      </linearGradient>
    </defs>
    <circle cx="12" cy="12" r="10.3" fill="none"
      stroke="url(#prIcon)" strokeWidth="1.2" opacity="0.85"/>
    <circle cx="12" cy="12" r="7" fill="none"
      stroke="url(#prIcon)" strokeWidth="1.0" opacity="0.6"/>
    <circle cx="12" cy="12" r="3" fill="url(#prIcon)"/>
    <g stroke="url(#prIcon)" strokeWidth="1" strokeLinecap="round">
      <line x1="12" y1="1.5" x2="12" y2="3"/>
      <line x1="12" y1="21" x2="12" y2="22.5"/>
      <line x1="1.5" y1="12" x2="3" y2="12"/>
      <line x1="21" y1="12" x2="22.5" y2="12"/>
    </g>
  </svg>
);

export default ThePronoia;
